import { NotificationVariation } from '@salecycle/ui-component-library';
import { AuthorisedRoute } from 'core/router/AuthorisedRoute';
import { Actions as NotificationBarActions } from 'core/ui/notificationBar';
import { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import { Selectors as RouterSelectors } from 'router';
import { Site } from 'sections/client-management/entities';
import SiteBlacklist from 'sections/client-management/ui/pages/client-settings/site-blacklist';
import { getActiveClientFromStateAndLevel } from 'sections/client-management/utils/ClientHelpers';
import { State } from 'store';
import { Actions as CampaignActions } from '../state/campaigns';
import CampaignsCalendar from './CampaignsCalendar/CampaignsCalendar';
import { CampaignsList } from './CampaignsList';
import CampaignEditor from './Editor';
import { Priorities } from './Priorities';
import { SplitTests } from './SplitTests';
import SplitTestEditor from './SplitTests/components/creationWizard/splitTestWizard';
import { NewCampaignWizardPage } from 'sections/campaigns/new/sections/new-campaign/components/NewCampaignWizardPage';
import { OffsiteCampaignEditorPage } from '../../offsite-campaigns/offsite-campaign-editor/components/OffsiteCampaignEditorPage';
import OffsiteCampaignsListPage from '../../offsite-campaigns/offsite-campaigns-list/OffsiteCampaignsListPage';
import { OffsiteCampaignRoute } from 'sections/offsite-campaigns/routes/offsite-campaign-routes';
import NewOffsiteCampaignWizardPage from 'sections/offsite-campaigns/offsite-campaign-create/components/NewOffsiteCampaignWizardPage';
import { OnsiteCampaignSection } from 'sections/onsite-campaigns/OnsiteCampaignSection';
import { BulkRetargetingCampaignSection } from 'sections/bulk-retargeting-campaigns/BulkRetargetingCampaignSection';

interface IStateProps {
  site?: Site;
}

interface IDispatchProps {
  showNotification: (variation: NotificationVariation, detail: string) => void;
  reduxFetchCampaigns: () => void;
}

type ICampaignsProps = IStateProps & IDispatchProps;

export enum CampaignRoute {
  HOME = '/sites/:siteId/campaigns',
  MPS_LIST = '/sites/:siteId/campaigns/campaigns/',
  MPS_EDIT = '/sites/:siteId/campaigns/campaigns/:campaignId',
  MPS_CALENDAR = '/sites/:siteId/campaigns/calendar',
  MPS_SPLIT_TESTS_CREATE = '/sites/:siteId/campaigns/split_tests/create',
  MPS_SPLIT_TESTS_EDIT = '/sites/:siteId/campaigns/split_tests/:splitTestId',
  MPS_SPLIT_TESTS_LIST = '/sites/:siteId/campaigns/split_tests',
  MPS_PRIORITIES = '/sites/:siteId/campaigns/priorities',
  MPS_BLACKLIST = '/sites/:siteId/campaigns/blacklist',
  CREATE_WIZARD_PAGE = '/sites/:siteId/campaigns/campaigns/newcampaign/:campaignWizardPage/',
  BULK_RETARGETING = '/sites/:siteId/campaigns/bulk-retargeting',
}

const Campaigns: FC<ICampaignsProps> = props => {
  const { reduxFetchCampaigns } = props;
  useEffect(() => {
    reduxFetchCampaigns();
  }, [reduxFetchCampaigns, props.site]);

  return (
    <>
      <AuthorisedRoute
        path="/sites/:siteId/campaigns/onsite"
        component={OnsiteCampaignSection}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        exact={true}
        path={CampaignRoute.CREATE_WIZARD_PAGE}
        component={NewCampaignWizardPage}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        exact={true}
        path={OffsiteCampaignRoute.CREATE_OFFSITE_WIZARD_PAGE}
        component={NewOffsiteCampaignWizardPage}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        exact={true}
        path={OffsiteCampaignRoute.OFFSITE_EDIT}
        component={OffsiteCampaignEditorPage}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        path={CampaignRoute.MPS_EDIT}
        component={CampaignEditor}
        requiredPermissions={['ManageCampaigns']}
        exact={true}
      />
      <AuthorisedRoute
        exact={true}
        path={CampaignRoute.MPS_LIST}
        component={CampaignsList}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        exact={true}
        path={OffsiteCampaignRoute.OFFSITE_LIST}
        component={OffsiteCampaignsListPage}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        path={CampaignRoute.MPS_CALENDAR}
        component={CampaignsCalendar}
        requiredPermissions={['ManageCampaigns']}
        requiredFlags={['campaigns-calendar']}
      />
      <AuthorisedRoute
        exact={true}
        path={CampaignRoute.MPS_SPLIT_TESTS_CREATE}
        component={SplitTestEditor}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        exact={true}
        path={CampaignRoute.MPS_SPLIT_TESTS_EDIT}
        component={SplitTestEditor}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        exact={true}
        path={CampaignRoute.MPS_SPLIT_TESTS_LIST}
        component={SplitTests}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        exact={true}
        path={CampaignRoute.MPS_PRIORITIES}
        component={Priorities}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        exact={true}
        path={CampaignRoute.MPS_BLACKLIST}
        component={SiteBlacklist}
        requiredPermissions={['ManageCampaigns']}
      />
      <AuthorisedRoute
        path={CampaignRoute.BULK_RETARGETING}
        component={BulkRetargetingCampaignSection}
        requiredPermissions={['ManageCampaigns']}
      />
    </>
  );
};

const mapStateToProps = (state: State): IStateProps => {
  const hasLevel = RouterSelectors.getClientLevel(state);
  const site = hasLevel === 'sites' ? getActiveClientFromStateAndLevel(state, hasLevel) : null;
  return {
    site,
  };
};

const actionCreators: IDispatchProps = {
  showNotification: NotificationBarActions.customNotification,
  reduxFetchCampaigns: () => CampaignActions.requestGetCollection({ clear: true }),
};

export default connect(mapStateToProps, actionCreators)(Campaigns);
